import { useMemo, useState, useCallback, useEffect } from "react";
import {
  DEFAULT_AVATAR,
  S3_ALLOWED_IMAGE_TYPES,
  S3_ALLOWED_DOCUMENT_TYPES,
  S3_ALLOWED_VIDEO_TYPES,
  S3_MAX_FILE_SIZE,
} from "../constants/common.constant";
import React from "react";

type S3FileType = "image" | "document" | "video";

interface S3HelperOptions {
  fallback?: string;
  type?: S3FileType;
  crossOrigin?: "anonymous" | "use-credentials";
}

/**
 * Helper function to get secure URL for S3 files
 * @param url - The S3 URL of the file
 * @param options - Configuration options
 * @returns Processed URL with referer parameter
 */
export const getS3FileUrl = (
  url: string | null,
  options: S3HelperOptions = {},
  customReferer?: string,
) => {
  const {
    fallback = DEFAULT_AVATAR,
    type = "image",
    crossOrigin = "anonymous",
  } = options;

  if (!url) return fallback;
  try {
    const fileUrl = new URL(url);
    const referer = customReferer || window.location.origin;
    fileUrl.searchParams.append("referer", referer);
    return fileUrl.toString();
  } catch {
    return fallback;
  }
};

export const getS3FileData: (
  url: string | null,
) => Promise<ArrayBuffer | null> = (url: string | null) => {
  return new Promise((resolve, reject) => {
    if (!url) return resolve(null);
    try {
      const fileUrl = getS3FileUrl(url);
      fetch(fileUrl)
        .then((response) => response.arrayBuffer())
        .then((arrayBuffer) => {
          resolve(arrayBuffer);
        })
        .catch((error) => {
          reject(error);
        });
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * React component to render S3 images with proper handling
 */
export const S3Image: React.FC<{
  src: string | null;
  alt?: string;
  className?: string;
  fallback?: string;
  crossOrigin?: "anonymous" | "use-credentials";
  cacheKey?: string;
}> = ({
  src,
  alt = "",
  className = "",
  fallback,
  crossOrigin = "anonymous",
  cacheKey,
}) => {
  const [error, setError] = useState(false);
  const [cachedUrl, setCachedUrl] = useState<string | null>(null);

  useEffect(() => {
    const cacheImage = async () => {
      if (!src) return;

      const key = cacheKey || src;

      // Try to get from cache first
      try {
        const cache = await caches.open("s3-image-cache");
        const cachedResponse = await cache.match(key);

        if (cachedResponse) {
          const blob = await cachedResponse.blob();
          setCachedUrl(URL.createObjectURL(blob));
          return;
        }

        // If not in cache, fetch and cache it
        const imageUrl = getS3FileUrl(src, { fallback });
        const response = await fetch(imageUrl);
        const responseClone = response.clone();
        await cache.put(key, responseClone);

        const blob = await response.blob();
        setCachedUrl(URL.createObjectURL(blob));
      } catch (err) {
        console.error("Caching failed:", err);
        setError(true);
      }
    };

    cacheImage();

    // Cleanup
    return () => {
      if (cachedUrl) {
        URL.revokeObjectURL(cachedUrl);
      }
    };
  }, [src, fallback, cacheKey]);

  const imageUrl = error
    ? fallback || DEFAULT_AVATAR
    : cachedUrl || getS3FileUrl(src, { fallback });

  return React.createElement("img", {
    src: imageUrl,
    alt,
    className,
    crossOrigin,
    onError: () => setError(true),
  });
};

/**
 * Hook to handle S3 file URLs
 */
export const useS3File = (
  url: string | null,
  options: S3HelperOptions = {},
) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const fileUrl = useMemo(() => {
    if (error) return options.fallback || DEFAULT_AVATAR;
    return getS3FileUrl(url, options);
  }, [url, error, options]);

  const handleError = useCallback(() => {
    setError(true);
    setLoading(false);
  }, []);

  const handleLoad = useCallback(() => {
    setLoading(false);
  }, []);

  return {
    url: fileUrl,
    error,
    loading,
    handleError,
    handleLoad,
  };
};

/**
 * Validate file before upload
 */
export const validateS3File = (
  file: File,
  type: S3FileType = "image",
): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > S3_MAX_FILE_SIZE) {
    return {
      valid: false,
      error: "File size exceeds 5MB limit",
    };
  }

  // Check file type
  const allowedTypes = {
    image: S3_ALLOWED_IMAGE_TYPES,
    document: S3_ALLOWED_DOCUMENT_TYPES,
    video: S3_ALLOWED_VIDEO_TYPES,
  }[type];

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed types: ${allowedTypes.join(", ")}`,
    };
  }

  return { valid: true };
};

/**
 * Prepare file for S3 upload
 */
export const prepareS3Upload = (file: File): FormData => {
  const formData = new FormData();
  formData.append("file", file);
  return formData;
};
